import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";

const ABOUT_BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
];

const MILESTONES = [
  ["Early Years", "Founded in Dubai", "Started as a boutique catering to UAE corporate travel."],
  ["Phase II", "Expanded into Asia", "Opened ground operations in Bangkok and Kuala Lumpur."],
  ["Phase III", "MICE Division Launch", "Dedicated conferences & incentives vertical."],
  ["Phase IV", "100,000-pax milestone", "Crossed six figures of travelers managed."],
  ["Phase V", "Philippines, Vietnam & Bali", "Rounded out our 7-destination portfolio."],
  ["Today", "140,000+ pax and counting", "Still Dubai-headquartered. Still partner-obsessed."],
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        path="/about"
        title="About — A Premium Destination Management Company for South East Asia & West Asia"
        description="Trawelldmc — Dubai-headquartered premium DMC. 20+ years, 140,000+ travelers, 7 destinations. Built on local ground teams, contracted hotel inventory, and two decades of vendor relationships."
        breadcrumb={ABOUT_BREADCRUMB}
      />
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-24 md:pt-32 pb-16">
        <div className="max-w-3xl">
          <div className="overline mb-5">About Trawelldmc</div>
          <h1 className="font-heading text-5xl md:text-7xl font-light tracking-tight text-[#0B192C] leading-[1.02]">
            A Dubai house with <br />
            <span className="text-[#FF5238]">twenty</span> years of range.
          </h1>
          <p className="text-slate-600 mt-8 text-lg leading-relaxed">
            We're not a travel-portal. We're not a tech platform. We're a destination management
            company — which means when your group lands, our people are on the ground, making sure
            it works. Twenty years. Seven countries. One hundred and forty thousand travelers. That's
            not a marketing line — it's our operating history.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16 grid grid-cols-2 md:grid-cols-4 gap-8 border-y border-slate-200">
        {[
          ["20+", "Years of operations"],
          ["140k+", "Travelers managed"],
          ["07", "Destinations"],
          ["48 hrs", "Avg proposal time"],
        ].map(([v, l]) => (
          <div key={l} className="border-l-2 border-[#FF5238] pl-5">
            <div className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">{v}</div>
            <div className="overline mt-2">{l}</div>
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">Our journey</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Two decades, <br />in plain terms.
          </h2>
        </div>

        <ol className="space-y-10">
          {MILESTONES.map(([year, title, copy], idx) => (
            <li key={year} className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid={`milestone-${year}`}>
              <div className="md:col-span-2 font-heading text-5xl font-light text-[#FF5238] tracking-tight">{year}</div>
              <div className="md:col-span-10 border-t border-slate-200 pt-4">
                <div className="font-heading text-2xl font-medium text-[#0B192C]">{title}</div>
                <p className="text-slate-600 mt-2 leading-relaxed max-w-2xl">{copy}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Brand Kit */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-24" data-testid="brand-kit-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center border border-slate-200 bg-white p-8 md:p-12">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Brand Kit</div>
            <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C]">
              Press & partners — <br />download the HD logo.
            </h2>
            <p className="text-slate-600 mt-5 leading-relaxed max-w-xl">
              For partnership decks, co-branded collateral, conference signage, or press mentions,
              grab our high-resolution logo asset below. Do not alter colors, proportions, or add effects.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="/trawelldmc-logo-hd.png"
                download="trawelldmc-logo-hd.png"
                className="btn-primary"
                data-testid="brand-kit-download-btn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>
                Download HD Logo (PNG)
              </a>
              <a
                href="/trawelldmc-logo-hd.png"
                target="_blank"
                rel="noreferrer"
                className="btn-ghost text-sm border border-slate-200 !py-3 !px-5"
                data-testid="brand-kit-preview-btn"
              >
                Open full-size preview →
              </a>
            </div>
            <div className="mt-6 flex flex-wrap gap-6 text-xs text-slate-500">
              <span>High-resolution PNG · transparent background-ready</span>
              <span>Colors — Navy #0B192C · Coral #FF5238</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-[#0B192C] border border-slate-200 p-8 flex items-center justify-center">
              <img
                src="/trawelldmc-logo-hd.png"
                alt="Trawelldmc HD logo preview"
                className="w-full max-w-sm h-auto"
                data-testid="brand-kit-logo-preview"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#0B192C] text-white py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="overline !text-[#FF5238] mb-5">Ready when you are</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
            Let's get your brief on paper.
          </h2>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/inquiry" className="btn-primary" data-testid="about-cta-inquiry">
              Submit a brief <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/quote" className="btn-secondary" data-testid="about-cta-quote">
              Try AI First Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
